import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { BarcodeFormat, DecodeHintType } from "@zxing/library";
import { Camera, X, AlertTriangle, RefreshCcw, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * BarcodeScanner
 *
 * iOS/Android/Desktop-safe camera scanner. Key design points:
 *
 *  - No `enumerateDevices()` before permission (iOS Safari returns
 *    labelless / empty devices). Direct `getUserMedia({facingMode:
 *    'environment'})` inside the caller's user gesture with a
 *    constraint-fallback ladder.
 *  - Orientation-agnostic viewport. The scan UI works identically in
 *    portrait and landscape — no forced orientation lock, no "please
 *    rotate" overlay. Layout uses `absolute` positioning + `object-fit:
 *    cover` on the video so both aspect ratios stay stable. Owner-Fix
 *    (05.09.2026): frühere Portrait-Lock + Rotations-Hinweis wurden
 *    entfernt, damit der POS-Scan an Terminals im Querformat nicht mehr
 *    blockiert wird.
 *  - Success feedback pipeline: on every accepted scan we (1) beep via
 *    WebAudio, (2) fire haptic vibration where supported, (3) show a
 *    green overlay with product name + EAN, (4) auto-close and hand back
 *    to the workflow. The `onDetected` callback is async and MUST return
 *    `{ ok: boolean, name?, ean?, message? }` — we display the outcome
 *    accordingly (green success or amber fail toast inside the scanner).
 *  - Multi-scan double-booking protection: while a scan is being handled
 *    the reader is soft-locked (`busy`); every code has a 1500 ms
 *    same-code cooldown; each scan resolves before the next one is
 *    accepted.
 *  - HID scanner + manual EAN entry are unaffected — this component only
 *    powers the camera path.
 */

// Default: klassische 1D-Produktbarcodes für den POS-Scan-Flow.
// Der optionale ``formats``-Prop erlaubt es Consumern, andere Formate
// zu aktivieren (z. B. QR_CODE für Club-Card-Scans) — bewusst KEINE
// Formatvermischung, damit Produkt- und Karten-Flows sich niemals
// gegenseitig triggern.
const DEFAULT_FORMATS = [
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.UPC_A,
  BarcodeFormat.UPC_E,
  BarcodeFormat.CODE_128,
  BarcodeFormat.CODE_39,
];

// Für Club-Card-Scans außerhalb dieser Komponente exportiert.
export const CARD_SCAN_FORMATS = [BarcodeFormat.QR_CODE];

const SAME_CODE_COOLDOWN_MS = 1500;
const SUCCESS_VISIBLE_MS = 900;
const FAIL_VISIBLE_MS = 1600;

function buildReader(formats) {
  const hints = new Map();
  hints.set(DecodeHintType.POSSIBLE_FORMATS, formats || DEFAULT_FORMATS);
  hints.set(DecodeHintType.TRY_HARDER, true);
  return new BrowserMultiFormatReader(hints);
}

function stopStream(stream) {
  try {
    stream?.getTracks?.().forEach((t) => {
      try { t.stop(); } catch {}
    });
  } catch {}
}

function playBeep() {
  // Short 880 Hz beep via WebAudio. Fully feature-gated: no error if the
  // browser blocks AudioContext (Safari before a user gesture, etc.).
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "sine";
    o.frequency.value = 880;
    g.gain.value = 0.001;
    g.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.12);
    o.connect(g).connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + 0.14);
    setTimeout(() => { try { ctx.close(); } catch {} }, 300);
  } catch {}
}

function vibrate() {
  // Stronger double-buzz for a clearer success cue on Android/Chrome.
  // iOS Safari currently ignores navigator.vibrate entirely — this is a
  // browser limitation, no workaround.
  try { navigator.vibrate?.([45, 60, 45]); } catch {}
}

function classifyError(err) {
  const name = err?.name || "";
  const msg = err?.message || "";
  if (name === "NotAllowedError" || name === "PermissionDeniedError")
    return { code: name, title: "Kamerazugriff verweigert",
             body: "Bitte in den Browser-Einstellungen den Kamerazugriff für diese Website erlauben und die Seite neu laden." };
  if (name === "NotFoundError" || name === "DevicesNotFoundError")
    return { code: name, title: "Keine Kamera gefunden",
             body: "Auf diesem Gerät wurde keine Kamera erkannt. Bitte manuellen Barcode eingeben." };
  if (name === "NotReadableError" || name === "TrackStartError")
    return { code: name, title: "Kamera belegt",
             body: "Die Kamera wird von einer anderen App belegt. Beende andere Kamera-Apps und versuche es erneut." };
  if (name === "OverconstrainedError" || name === "ConstraintNotSatisfiedError")
    return { code: name, title: "Kamera-Constraint nicht erfüllt",
             body: "Die gewünschte Kamera-Konfiguration wird nicht unterstützt." };
  if (name === "SecurityError")
    return { code: name, title: "Zugriff aus Sicherheitsgründen blockiert",
             body: "Der Browser hat den Kamerazugriff blockiert. Die Seite muss über HTTPS ausgeliefert werden." };
  if (name === "AbortError")
    return { code: name, title: "Kamera-Start abgebrochen",
             body: "Der Kamera-Start wurde abgebrochen. Bitte erneut starten." };
  if (name === "TypeError")
    return { code: name, title: "Kamera-API nicht verfügbar",
             body: "getUserMedia() ist nicht verfügbar. Meist liegt das an unsicherem Kontext (HTTP statt HTTPS)." };
  return { code: name || "UNKNOWN", title: "Kamera konnte nicht gestartet werden",
           body: msg || "Unbekannter Fehler beim Starten der Kamera." };
}

function preflight() {
  if (typeof window === "undefined")
    return { ok: false, code: "MISSING_API", title: "Nicht unterstützt", body: "Kein Fenster-Kontext." };
  if (window.isSecureContext === false)
    return { ok: false, code: "INSECURE_CONTEXT", title: "Unsicherer Kontext",
             body: "Kamera-Zugriff erfordert HTTPS. Bitte die App über eine https:// URL öffnen." };
  if (!navigator?.mediaDevices?.getUserMedia)
    return { ok: false, code: "MISSING_API", title: "Kamera-API nicht verfügbar",
             body: "Dein Browser unterstützt die Camera-API nicht. Auf iPhone benötigst du Safari (iOS 11+), auf Android Chrome/Edge/Firefox." };
  return { ok: true };
}

export default function BarcodeScanner({ open, onDetected, onClose, formats, successTitle, footerHint, facingMode, neutralUi, neutralTitle, neutralHint }) {
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const streamRef = useRef(null);
  const lastScanRef = useRef({ code: "", ts: 0 });
  const busyRef = useRef(false);

  const [stage, setStage] = useState("idle");        // idle|starting|live|error
  const [errInfo, setErrInfo] = useState(null);
  const [successInfo, setSuccessInfo] = useState(null); // {name, ean}
  const [failInfo, setFailInfo] = useState(null);       // {code, message}
  const [flashKey, setFlashKey] = useState(0);

  const onDetectedRef = useRef(onDetected);
  useEffect(() => { onDetectedRef.current = onDetected; }, [onDetected]);

  // Owner-Fix (05.09.2026): kein Orientation-Tracking, kein Portrait-
  // Lock, kein Rotations-Overlay mehr. Der Scanner soll in Portrait und
  // Landscape identisch funktionieren.

  const stopAll = useCallback(() => {
    try { controlsRef.current?.stop?.(); } catch {}
    controlsRef.current = null;
    stopStream(streamRef.current);
    streamRef.current = null;
    if (videoRef.current) {
      try { videoRef.current.srcObject = null; } catch {}
    }
  }, []);

  const handleCode = useCallback(async (code) => {
    if (busyRef.current) return;
    const now = Date.now();
    if (code === lastScanRef.current.code &&
        now - lastScanRef.current.ts < SAME_CODE_COOLDOWN_MS) return;
    lastScanRef.current = { code, ts: now };
    busyRef.current = true;
    setFlashKey((k) => k + 1);
    playBeep();
    vibrate();

    let result = null;
    try {
      result = await onDetectedRef.current?.(code);
    } catch (e) {
      result = { ok: false, message: e?.message || "Fehler bei der Verarbeitung" };
    }

    if (result && result.ok) {
      setSuccessInfo({ name: result.name || "Produkt erkannt", ean: result.ean || code });
      setFailInfo(null);
      // Give the user ~900 ms to see the confirmation, then close the
      // camera and hand back to the workflow.
      setTimeout(() => {
        setSuccessInfo(null);
        busyRef.current = false;
        if (open) onClose?.();
      }, SUCCESS_VISIBLE_MS);
    } else {
      setSuccessInfo(null);
      setFailInfo({
        code,
        message: result?.message || `Kein Produkt für „${code}" gefunden`,
      });
      setTimeout(() => {
        setFailInfo(null);
        busyRef.current = false;
      }, FAIL_VISIBLE_MS);
    }
  }, [open, onClose]);

  const start = useCallback(async () => {
    setErrInfo(null);
    const pre = preflight();
    if (!pre.ok) { setErrInfo(pre); setStage("error"); return; }
    setStage("starting");

    const attempts = [];
    // POS Final UX (04.09.2026) — bevorzugte Kamera per Prop steuerbar.
    // Der POS ruft mit `facingMode="user"` (Frontkamera) — Kunde blickt
    // aufs Terminal, Kamera zeigt nach vorn. Wenn der Prop fehlt, bleibt
    // der klassische Rückkamera-Pfad aktiv (Warenausgang, Warenannahme).
    const preferred = facingMode === "user" ? "user" : "environment";
    const fallback = preferred === "user" ? "environment" : "user";
    attempts.push({ video: { facingMode: { ideal: preferred }, width: { ideal: 1280 }, height: { ideal: 720 } }, audio: false });
    attempts.push({ video: { facingMode: preferred }, audio: false });
    attempts.push({ video: { facingMode: fallback }, audio: false });
    attempts.push({ video: true, audio: false });

    let stream = null;
    let lastErr = null;
    for (const constraints of attempts) {
      try { stream = await navigator.mediaDevices.getUserMedia(constraints); break; }
      catch (e) {
        lastErr = e;
        if (e?.name !== "OverconstrainedError" &&
            e?.name !== "ConstraintNotSatisfiedError" &&
            e?.name !== "NotFoundError") break;
      }
    }
    if (!stream) { setErrInfo(classifyError(lastErr)); setStage("error"); return; }
    streamRef.current = stream;

    const videoEl = videoRef.current;
    if (!videoEl) {
      stopStream(stream); streamRef.current = null;
      setErrInfo({ code: "NO_VIDEO_ELEMENT", title: "Videoelement fehlt", body: "Interner Fehler." });
      setStage("error");
      return;
    }
    videoEl.setAttribute("playsinline", "true");
    videoEl.setAttribute("webkit-playsinline", "true");
    videoEl.muted = true;
    videoEl.autoplay = true;
    videoEl.srcObject = stream;
    try { await videoEl.play(); } catch {}

    try {
      const reader = buildReader(formats);
      const controls = await reader.decodeFromVideoElement(videoEl, (result) => {
        if (!result) return;
        handleCode(result.getText());
      });
      controlsRef.current = controls;
      setStage("live");
    } catch (e) {
      stopStream(stream); streamRef.current = null;
      setErrInfo(classifyError(e)); setStage("error");
    }
  }, [handleCode, formats, facingMode]);

  useEffect(() => {
    if (open) { start(); }
    else { stopAll(); setStage("idle"); setErrInfo(null); setSuccessInfo(null); setFailInfo(null); busyRef.current = false; }
    return () => { stopAll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const close = useCallback(() => { stopAll(); onClose?.(); }, [stopAll, onClose]);

  // Escape-Handler auf Capture-Ebene: verhindert, dass ein umschließender
  // Radix-Dialog (z. B. Produkte-Formular) durch ESC mitgeschlossen wird.
  // Solange der Scanner offen ist, gehört ESC ihm.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        e.preventDefault();
        close();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, close]);

  if (!open) return null;

  // Ins <body> portalieren + z-Index über Radix Dialog (z-50) heben, damit
  // der Scanner auch aus einem Dialog heraus (Produkte-Formular) über dem
  // DialogOverlay erscheint und klickbar bleibt.
  //
  // `pointer-events-auto` ist zwingend: Radix setzt bei offenem modalem
  // Dialog `document.body { pointer-events: none }` — ohne den Reset erben
  // wir das im Portal und der Scanner ist nicht hit-testbar.
  //
  // stopPropagation auf allen Pointer-Events verhindert, dass Radix den
  // Klick als "Outside-Click" wertet und den darunterliegenden Dialog
  // mitschließt.
  const stop = (e) => e.stopPropagation();
  const content = (
    <div
      className="fixed inset-0 z-[100] bg-black pointer-events-auto"
      data-testid="barcode-scanner"
      onPointerDown={stop}
      onPointerDownCapture={stop}
      onMouseDown={stop}
      onMouseDownCapture={stop}
      onTouchStart={stop}
      onClick={stop}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 bg-zinc-950/85 px-4 py-3 flex items-center justify-between border-b border-zinc-800 z-10">
        <div className="flex items-center gap-2 text-cyan-400 font-display uppercase tracking-wider">
          <Camera size={18} />
          <span data-testid="scanner-stage">
            {stage === "live" && "Scan aktiv"}
            {stage === "starting" && "Kamera wird gestartet…"}
            {stage === "idle" && "Kamera bereit"}
            {stage === "error" && "Kamera-Fehler"}
          </span>
        </div>
        <Button
          data-testid="scanner-close-btn"
          variant="ghost"
          size="icon"
          className="text-zinc-100 hover:bg-zinc-800"
          onClick={close}
        >
          <X />
        </Button>
      </div>

      {/* Video — POS Final UX (04.09.2026):
          Wenn `neutralUi` gesetzt ist, blenden wir das Live-Kamerabild
          unter einem opaken Neutrale-Scan-Overlay aus. Der Videostream
          läuft technisch weiter (ZXing braucht die Frames für die
          Erkennung), er ist optisch aber nicht als „Selfie-Kamera"
          sichtbar — der Kunde sieht eine ruhige Scan-Anleitung. */}
      <video
        ref={videoRef}
        data-testid="scanner-video"
        className={`absolute inset-0 w-full h-full object-cover ${neutralUi ? "opacity-0 pointer-events-none" : ""}`}
        muted
        autoPlay
        playsInline
      />
      {neutralUi && stage !== "error" && (
        <div
          className="absolute inset-0 bg-zinc-950 flex flex-col items-center justify-center px-6 z-[5]"
          data-testid="scanner-neutral-ui"
        >
          <div className="text-center max-w-xs">
            <div className="font-display text-xl sm:text-2xl font-black uppercase tracking-widest text-cyan-400 mb-3">
              {neutralTitle || "Produkt scannen"}
            </div>
            <div className="text-sm text-zinc-300 leading-relaxed">
              {neutralHint || "Produkt in 15–20 cm Abstand vor die Kamera halten."}
            </div>
          </div>
          {/* Neutrale Scan-Rahmen-Illustration: nur SVG, kein Kamerabild. */}
          <div className="relative mt-8 w-64 h-40 sm:w-72 sm:h-48">
            <svg viewBox="0 0 200 120" className="w-full h-full">
              {/* Ecken */}
              <path d="M10 30 L10 10 L30 10" stroke="rgb(34 211 238)" strokeWidth="3" fill="none" strokeLinecap="round" />
              <path d="M170 10 L190 10 L190 30" stroke="rgb(34 211 238)" strokeWidth="3" fill="none" strokeLinecap="round" />
              <path d="M190 90 L190 110 L170 110" stroke="rgb(34 211 238)" strokeWidth="3" fill="none" strokeLinecap="round" />
              <path d="M30 110 L10 110 L10 90" stroke="rgb(34 211 238)" strokeWidth="3" fill="none" strokeLinecap="round" />
              {/* Barcode-Streifen als Deko */}
              <g opacity="0.65">
                <rect x="30" y="35" width="3" height="50" fill="rgb(113 113 122)" />
                <rect x="38" y="35" width="6" height="50" fill="rgb(113 113 122)" />
                <rect x="49" y="35" width="2" height="50" fill="rgb(113 113 122)" />
                <rect x="56" y="35" width="4" height="50" fill="rgb(113 113 122)" />
                <rect x="66" y="35" width="8" height="50" fill="rgb(113 113 122)" />
                <rect x="80" y="35" width="3" height="50" fill="rgb(113 113 122)" />
                <rect x="88" y="35" width="5" height="50" fill="rgb(113 113 122)" />
                <rect x="98" y="35" width="2" height="50" fill="rgb(113 113 122)" />
                <rect x="105" y="35" width="6" height="50" fill="rgb(113 113 122)" />
                <rect x="116" y="35" width="4" height="50" fill="rgb(113 113 122)" />
                <rect x="125" y="35" width="7" height="50" fill="rgb(113 113 122)" />
                <rect x="137" y="35" width="3" height="50" fill="rgb(113 113 122)" />
                <rect x="145" y="35" width="5" height="50" fill="rgb(113 113 122)" />
                <rect x="155" y="35" width="2" height="50" fill="rgb(113 113 122)" />
                <rect x="162" y="35" width="8" height="50" fill="rgb(113 113 122)" />
              </g>
              {/* Animierte Scan-Linie */}
              <line
                x1="15" x2="185" y1="60" y2="60"
                stroke="rgb(34 211 238)" strokeWidth="2" strokeLinecap="round"
                style={{
                  filter: "drop-shadow(0 0 6px rgba(34,211,238,0.9))",
                  animation: "scanner-line 1.8s ease-in-out infinite",
                  transformOrigin: "center",
                }}
              />
            </svg>
            <style>{`@keyframes scanner-line {
              0%,100% { transform: translateY(-24px); opacity: .5; }
              50% { transform: translateY(24px); opacity: 1; }
            }`}</style>
          </div>
          <div className="mt-6 text-[11px] uppercase tracking-widest text-zinc-500">
            {stage === "starting" ? "Kamera wird vorbereitet …" : "Erkennung läuft …"}
          </div>
          {/* POS Final Fixes (04.09.2026) — großer sichtbarer Zurück-
              Button direkt unter „Erkennung läuft …". Der dezente X
              oben rechts bleibt als Fallback, ist aber nicht mehr der
              primäre Exit-Weg. Grosse Touch-Fläche, in beiden Themes
              gut lesbar. */}
          <Button
            data-testid="scanner-return-btn"
            onClick={close}
            className="mt-6 w-full max-w-xs h-14 bg-cyan-500 hover:bg-cyan-400 text-zinc-950 rounded-xl font-bold uppercase tracking-widest text-sm"
          >
            <X size={18} className="mr-2" />
            Zurück zur Kasse
          </Button>
        </div>
      )}
      <div className={`scanner-bracket ${neutralUi ? "hidden" : ""}`} />

      {/* Success flash — mount on every new scan (key change re-plays CSS anim) */}
      {successInfo && <div key={flashKey} className="scanner-flash" />}

      {/* Success card */}
      {successInfo && (
        <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 max-w-md mx-auto z-20"
             data-testid="scanner-success">
          <div className="bg-emerald-500/95 text-zinc-950 rounded-md p-5 shadow-2xl">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={36} className="shrink-0" />
              <div className="min-w-0">
                <div className="font-display font-black uppercase tracking-wide text-lg">
                  {successTitle || "Produkt erkannt"}
                </div>
                <div className="font-semibold text-base truncate" data-testid="scanner-success-name">
                  {successInfo.name}
                </div>
                <div className="font-mono text-xs opacity-80" data-testid="scanner-success-ean">
                  {successInfo.ean}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Fail card — soft failure (code not found). Scanner stays open. */}
      {failInfo && !successInfo && (
        <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 max-w-md mx-auto z-20"
             data-testid="scanner-scan-fail">
          <div className="bg-amber-500/95 text-zinc-950 rounded-md p-4 shadow-2xl">
            <div className="font-display font-bold uppercase tracking-wide text-sm">
              {failInfo.message}
            </div>
            <div className="font-mono text-xs opacity-80 mt-1">Code: {failInfo.code}</div>
          </div>
        </div>
      )}

      {/* Owner-Fix (05.09.2026): Landscape-Veil entfernt — Scanner ist
          orientation-agnostic. */}

      {/* Error card */}
      {stage === "error" && errInfo && (
        <div className="absolute inset-x-4 top-1/2 -translate-y-1/2 max-w-md mx-auto z-20"
             data-testid="scanner-error">
          <div className="bg-zinc-950 border border-rose-500/60 rounded-md p-5 space-y-3">
            <div className="flex items-center gap-2 text-rose-400">
              <AlertTriangle size={20} />
              <div className="font-display font-bold uppercase tracking-wider text-sm">
                {errInfo.title}
              </div>
            </div>
            <div className="text-sm text-zinc-300 leading-relaxed">{errInfo.body}</div>
            <div className="text-[10px] font-mono text-zinc-500" data-testid="scanner-error-code">
              Diagnose-Code: {errInfo.code}
            </div>
            <div className="flex gap-2 pt-1">
              <Button
                data-testid="scanner-retry-btn"
                onClick={start}
                className="flex-1 bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-sm uppercase tracking-wider font-semibold"
              >
                <RefreshCcw size={14} className="mr-2" /> Kamera starten
              </Button>
              <Button variant="outline" className="border-zinc-700" onClick={close}>Abbrechen</Button>
            </div>
          </div>
        </div>
      )}

      {/* Footer hint */}
      {stage === "live" && !successInfo && !failInfo && !neutralUi && (
        <div className="absolute bottom-0 left-0 right-0 bg-zinc-950/85 border-t border-zinc-800 px-4 py-4 text-center text-sm text-zinc-400">
          {footerHint || "Halte den Barcode ruhig im Rahmen. Nach jedem Scan schließt sich die Kamera automatisch."}
        </div>
      )}
      {stage === "starting" && !neutralUi && (
        <div className="absolute bottom-0 left-0 right-0 bg-zinc-950/85 border-t border-zinc-800 px-4 py-4 text-center text-sm text-zinc-400">
          Erlaube den Kamerazugriff, wenn dein Browser danach fragt.
        </div>
      )}
    </div>
  );
  return createPortal(content, document.body);
}
